import { useState, useEffect, useRef, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback } from './ui/avatar';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import { Tooltip, TooltipTrigger, TooltipContent } from './ui/tooltip';
import { Share2, X, Loader2, Check, Users, Mail, AlertCircle, RefreshCw, Search, UserPlus, Eye, Play, ShieldX, Trash2, ChevronDown, ChevronUp, Clock, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import { searchTeachers, shareLesson, getLessonShares, revokeShareLesson, getAllTeachers } from '../../utils/supabase/queries';
import { getCurrentUser } from '../../utils/supabase/client';

interface SharedTeacher {
  id: string;
  full_name: string;
  email?: string;
}

interface Share {
  id: string;
  lesson_id: string;
  shared_to_teacher_id: string;
  shared_to_profile: SharedTeacher | SharedTeacher[];
  created_at: string;
}

interface ShareLessonModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lessonId: string;
  lessonTitle: string;
  onShareSuccess?: () => void;
}

/** Get initials from a full name (max 2 characters). */
function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

/** Friendly relative time string (e.g. "2 days ago"). */
function timeAgo(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

// Color palette for avatar backgrounds — deterministic by name
const AVATAR_COLORS = [
  'bg-indigo-100 text-indigo-700',
  'bg-rose-100 text-rose-700',
  'bg-amber-100 text-amber-700',
  'bg-emerald-100 text-emerald-700',
  'bg-sky-100 text-sky-700',
  'bg-violet-100 text-violet-700',
  'bg-teal-100 text-teal-700',
  'bg-pink-100 text-pink-700',
];

function avatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

export function ShareLessonModal({
  open,
  onOpenChange,
  lessonId,
  lessonTitle,
  onShareSuccess,
}: ShareLessonModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [availableTeachers, setAvailableTeachers] = useState<SharedTeacher[]>([]);
  const [sharedWith, setSharedWith] = useState<Share[]>([]);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sharingId, setSharingId] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string>('');
  const [revoking, setRevoking] = useState<string | null>(null);
  const [showAllTeachers, setShowAllTeachers] = useState(false);
  const [allTeachers, setAllTeachers] = useState<SharedTeacher[]>([]);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Helper to get profile name from either array or object
  const getProfileName = (profile: SharedTeacher | SharedTeacher[]): string => {
    if (Array.isArray(profile)) {
      return profile[0]?.full_name || 'Unknown';
    }
    return profile?.full_name || 'Unknown';
  };

  // Helper to get profile email from either array or object
  const getProfileEmail = (profile: SharedTeacher | SharedTeacher[]): string | undefined => {
    if (Array.isArray(profile)) {
      return profile[0]?.email;
    }
    return profile?.email;
  };

  // Filtered all-teachers list (exclude self + already shared)
  const filteredAllTeachers = useMemo(
    () =>
      allTeachers.filter((t) => {
        if (t.id === currentUserId) return false;
        return !sharedWith.some((s) => s.shared_to_teacher_id === t.id);
      }),
    [allTeachers, currentUserId, sharedWith],
  );

  // Auto-focus search input on open
  useEffect(() => {
    if (open) {
      loadInitialData();
      setTimeout(() => searchInputRef.current?.focus(), 150);
    } else {
      // Reset state on close
      setSearchQuery('');
      setAvailableTeachers([]);
      setShowAllTeachers(false);
    }
  }, [open]);

  // Search effect
  useEffect(() => {
    if (searchQuery.trim().length === 0) {
      setAvailableTeachers([]);
      return;
    }

    const timer = setTimeout(() => {
      performSearch();
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const loadInitialData = async () => {
    try {
      setLoading(true);

      // Get current user
      const user = await getCurrentUser();
      if (user) {
        setCurrentUserId(user.id);
      }

      // Load existing shares
      const shares = await getLessonShares(lessonId);
      const sharesArray = Array.isArray(shares) ? shares : Object.values(shares || {});
      setSharedWith(sharesArray as Share[]);

      // Load all teachers for backup display
      const teachers = await getAllTeachers();
      const teachersArray = Array.isArray(teachers) ? teachers : Object.values(teachers || {});
      setAllTeachers(teachersArray as SharedTeacher[]);
    } catch (error) {
      console.error('Error loading initial data:', error);
      toast.error('Failed to load sharing information');
    } finally {
      setLoading(false);
    }
  };

  const performSearch = async () => {
    if (!searchQuery.trim()) {
      setAvailableTeachers([]);
      return;
    }

    try {
      setSearching(true);
      const teachers = await searchTeachers(searchQuery, currentUserId, lessonId);
      const teachersArray = Array.isArray(teachers) ? teachers : Object.values(teachers || {});
      setAvailableTeachers(teachersArray as SharedTeacher[]);

      // If searching by email and found nothing, log a warning
      if (searchQuery.includes('@') && teachers.length === 0) {
        console.warn(`No teachers found with email containing: ${searchQuery}`);
        console.info(
          `Available teachers: ${allTeachers.map((t) => `${t.full_name} (${t.email || 'NO EMAIL'})`).join(', ')}`,
        );
      }
    } catch (error) {
      console.error('Error searching teachers:', error);
      toast.error('Failed to search teachers');
    } finally {
      setSearching(false);
    }
  };

  const handleShareLesson = async (teacherId: string, source: 'search' | 'all' = 'search') => {
    try {
      setSharingId(teacherId);
      const teacher =
        source === 'search'
          ? availableTeachers.find((t) => t.id === teacherId) ?? allTeachers.find((t) => t.id === teacherId)
          : allTeachers.find((t) => t.id === teacherId);
      if (!teacher) return;

      await shareLesson(lessonId, currentUserId, teacherId);

      // Reload shared teachers
      const shares = await getLessonShares(lessonId);
      const sharesArray = Array.isArray(shares) ? shares : Object.values(shares || {});
      setSharedWith(sharesArray as Share[]);

      // Clear search
      setSearchQuery('');
      setAvailableTeachers([]);

      toast.success(`Lesson shared with ${teacher.full_name}`);
      onShareSuccess?.();
    } catch (error) {
      console.error('Error sharing lesson:', error);
      toast.error('Failed to share lesson');
    } finally {
      setSharingId(null);
    }
  };

  const handleRevokeShare = async (shareId: string) => {
    try {
      setRevoking(shareId);
      await revokeShareLesson(shareId, currentUserId);

      // Reload shared teachers
      const shares = await getLessonShares(lessonId);
      const sharesArray = Array.isArray(shares) ? shares : Object.values(shares || {});
      setSharedWith(sharesArray as Share[]);

      toast.success('Share revoked');
      onShareSuccess?.();
    } catch (error) {
      console.error('Error revoking share:', error);
      toast.error('Failed to revoke share');
    } finally {
      setRevoking(null);
    }
  };

  // ─── Render helpers ──────────────────────────────────────────────

  /** Render a single teacher row (search results or all-teachers list). */
  const TeacherRow = ({
    teacher,
    compact = false,
    source = 'search',
  }: {
    teacher: SharedTeacher;
    compact?: boolean;
    source?: 'search' | 'all';
  }) => {
    const isSharing = sharingId === teacher.id;
    return (
      <div
        className={`group flex items-center gap-3 rounded-xl border border-border/60 bg-background
          transition-all duration-150 hover:border-primary/30 hover:shadow-sm
          ${compact ? 'px-3 py-2' : 'px-4 py-3'}`}
      >
        <Avatar className={`${compact ? 'size-8' : 'size-10'} shrink-0`}>
          <AvatarFallback className={`${avatarColor(teacher.full_name)} text-xs font-semibold`}>
            {getInitials(teacher.full_name)}
          </AvatarFallback>
        </Avatar>

        <div className="flex-1 min-w-0">
          <p className={`font-medium truncate ${compact ? 'text-sm' : 'text-sm'}`}>{teacher.full_name}</p>
          {teacher.email ? (
            <p className="text-xs text-muted-foreground truncate flex items-center gap-1">
              <Mail className="w-3 h-3 shrink-0" />
              {teacher.email}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground/60 italic">No email registered</p>
          )}
        </div>

        <Button
          size="sm"
          onClick={() => handleShareLesson(teacher.id, source)}
          disabled={!!sharingId}
          className="shrink-0 gap-1.5 rounded-lg"
        >
          {isSharing ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <UserPlus className="w-3.5 h-3.5" />
          )}
          {!compact && <span>Share</span>}
        </Button>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] gap-0 p-0 overflow-hidden">
        {/* ── Header ─────────────────────────────────────────────── */}
        <div className="bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-pink-500/10 px-6 pt-6 pb-4">
          <DialogHeader className="space-y-1.5">
            <DialogTitle className="text-xl font-bold flex items-center gap-2.5">
              <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-primary/10 text-primary">
                <Share2 className="w-5 h-5" />
              </div>
              Share Lesson
            </DialogTitle>
            <DialogDescription className="text-sm leading-relaxed">
              Share <span className="font-medium text-foreground">{lessonTitle}</span> with other teachers.
              They can teach it to students but cannot edit it.
            </DialogDescription>
          </DialogHeader>
        </div>

        <ScrollArea className="max-h-[calc(90vh-180px)]">
          <div className="px-6 py-5 space-y-5">
            {/* ── Search Teachers ──────────────────────────────────── */}
            <div className="space-y-2.5">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <Input
                  ref={searchInputRef}
                  id="search-teacher"
                  placeholder="Search by name or email…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="rounded-xl pl-9 pr-9 h-11 bg-muted/40 border-border/60 focus:bg-background transition-colors"
                />
                {searching && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
                )}
                {!searching && searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <p className="text-xs text-muted-foreground pl-1">
                Find teachers to collaborate with by name or email address
              </p>
            </div>

            {/* ── Search Results ───────────────────────────────────── */}
            {searchQuery.trim().length > 0 && (
              <div className="space-y-3">
                {/* Results header */}
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="text-xs font-normal px-2 py-0.5">
                    {searching ? 'Searching…' : `${availableTeachers.length} result${availableTeachers.length !== 1 ? 's' : ''}`}
                  </Badge>
                </div>

                {/* No results */}
                {availableTeachers.length === 0 && !searching && (
                  <div className="space-y-3">
                    <div className="bg-amber-50/80 dark:bg-amber-950/20 border border-amber-200/60 dark:border-amber-800/40 rounded-xl p-4 flex items-start gap-3">
                      <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/30 shrink-0">
                        <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">No matches found</p>
                        <p className="text-xs text-amber-800/80 dark:text-amber-300/60 mt-0.5 leading-relaxed">
                          {searchQuery.includes('@')
                            ? 'That email wasn\'t found. Double-check the address or try searching by name.'
                            : 'Try a different name or use their email address instead.'}
                        </p>
                      </div>
                    </div>

                    {/* Show all teachers fallback */}
                    {filteredAllTeachers.length > 0 && (
                      <div className="space-y-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setShowAllTeachers(!showAllTeachers)}
                          className="w-full text-xs rounded-lg gap-1.5 h-9"
                        >
                          {showAllTeachers ? (
                            <ChevronUp className="w-3.5 h-3.5" />
                          ) : (
                            <ChevronDown className="w-3.5 h-3.5" />
                          )}
                          {showAllTeachers ? 'Hide' : 'Browse'} all teachers ({filteredAllTeachers.length})
                        </Button>

                        {showAllTeachers && (
                          <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
                            {filteredAllTeachers.map((teacher) => (
                              <TeacherRow key={teacher.id} teacher={teacher} compact source="all" />
                            ))}
                            {filteredAllTeachers.length === 0 && (
                              <p className="text-xs text-muted-foreground text-center py-3">
                                No more teachers available to share with
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Results list */}
                {availableTeachers.length > 0 && (
                  <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                    {availableTeachers.map((teacher) => (
                      <TeacherRow key={teacher.id} teacher={teacher} />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── Loading skeleton ─────────────────────────────────── */}
            {loading && sharedWith.length === 0 && (
              <div className="space-y-3">
                {[1, 2].map((i) => (
                  <div key={i} className="flex items-center gap-3 px-4 py-3 rounded-xl border border-border/40 animate-pulse">
                    <div className="w-10 h-10 rounded-full bg-muted" />
                    <div className="flex-1 space-y-2">
                      <div className="h-3.5 w-32 bg-muted rounded" />
                      <div className="h-3 w-44 bg-muted/60 rounded" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ── Shared With ──────────────────────────────────────── */}
            <div className="space-y-3">
              <Separator />
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-sm flex items-center gap-2">
                  <Users className="w-4 h-4 text-muted-foreground" />
                  Currently Shared With
                </h3>
                {sharedWith.length > 0 && (
                  <Badge variant="secondary" className="text-xs px-2 py-0.5 tabular-nums">
                    {sharedWith.length}
                  </Badge>
                )}
              </div>

              {sharedWith.length === 0 && !loading ? (
                <div className="text-center py-10 rounded-xl border border-dashed border-border/60 bg-muted/20">
                  <div className="flex justify-center mb-3">
                    <div className="w-14 h-14 rounded-full bg-muted/50 flex items-center justify-center">
                      <UserCheck className="w-7 h-7 text-muted-foreground/30" />
                    </div>
                  </div>
                  <p className="text-sm font-medium text-muted-foreground">Not shared with anyone yet</p>
                  <p className="text-xs text-muted-foreground/60 mt-1">
                    Use the search above to find colleagues
                  </p>
                </div>
              ) : (
                <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                  {sharedWith.map((share) => {
                    const name = getProfileName(share.shared_to_profile);
                    const email = getProfileEmail(share.shared_to_profile);
                    const isRevoking = revoking === share.id;

                    return (
                      <div
                        key={share.id}
                        className="group flex items-center gap-3 px-4 py-3 rounded-xl border border-border/40 bg-muted/20 transition-all duration-150 hover:bg-muted/40"
                      >
                        <Avatar className="size-10 shrink-0">
                          <AvatarFallback className={`${avatarColor(name)} text-xs font-semibold`}>
                            {getInitials(name)}
                          </AvatarFallback>
                        </Avatar>

                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{name}</p>
                          {email && (
                            <p className="text-xs text-muted-foreground truncate flex items-center gap-1">
                              <Mail className="w-3 h-3 shrink-0" />
                              {email}
                            </p>
                          )}
                          <p className="text-xs text-muted-foreground/60 flex items-center gap-1 mt-0.5">
                            <Clock className="w-3 h-3 shrink-0" />
                            Shared {timeAgo(share.created_at)}
                          </p>
                        </div>

                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => handleRevokeShare(share.id)}
                              disabled={isRevoking}
                              className="shrink-0 size-8 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              {isRevoking ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <X className="w-4 h-4" />
                              )}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="left">Revoke access</TooltipContent>
                        </Tooltip>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* ── Permissions Info ─────────────────────────────────── */}
            <div className="rounded-xl border border-indigo-200/60 dark:border-indigo-800/40 bg-indigo-50/50 dark:bg-indigo-950/20 p-4 space-y-3">
              <p className="text-sm font-semibold text-indigo-900 dark:text-indigo-200 flex items-center gap-2">
                <ShieldX className="w-4 h-4" />
                Sharing Permissions
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {[
                  { icon: Eye, label: 'View & teach this lesson', allowed: true },
                  { icon: Play, label: 'Create live sessions', allowed: true },
                  { icon: Share2, label: 'Share with other teachers', allowed: true },
                  { icon: ShieldX, label: 'Edit lesson content', allowed: false },
                  { icon: Trash2, label: 'Delete the lesson', allowed: false },
                ].map(({ icon: Icon, label, allowed }) => (
                  <div
                    key={label}
                    className={`flex items-center gap-2 text-xs rounded-lg px-3 py-2
                      ${allowed
                        ? 'bg-emerald-100/60 dark:bg-emerald-900/20 text-emerald-800 dark:text-emerald-300'
                        : 'bg-rose-100/60 dark:bg-rose-900/20 text-rose-800 dark:text-rose-300'
                      }`}
                  >
                    <Icon className="w-3.5 h-3.5 shrink-0" />
                    <span className="leading-tight">{label}</span>
                    <span className="ml-auto font-medium">{allowed ? '✓' : '✗'}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </ScrollArea>

        {/* ── Footer ────────────────────────────────────────────── */}
        <DialogFooter className="px-6 py-4 border-t bg-muted/20">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-lg">
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
